/**
 * "Say this in that voice" - the synthesis control under one announcement.
 *
 * Why it exists. Twilio has no Estonian voice, so an Estonian announcement has
 * to arrive as a file. Until this existed the only files were the ones that ship
 * with the app, which covered exactly the sentences WE wrote: the moment an
 * operator reworded the notice for their own lawyer they had a text Twilio
 * cannot read and no way to make a recording of it. This is that way - pick a
 * voice, press the button, and the chosen service reads the field aloud.
 *
 * WHICH service is not this component's business. The API answers with a list of
 * ids and labels and this renders them, so moving from the University of Tartu's
 * free synthesis to an ElevenLabs voice changes what is in the menu and nothing
 * about the control. The one thing it does have to handle is the SWITCH: a
 * saved clip's voice belongs to whichever provider made it, so `initialVoice`
 * decides what the picker opens on rather than the stored value doing it
 * directly - see `noticeClip.ts`.
 *
 * BOTH SERVICES ARE IN THE ONE MENU, under a heading each. The list used to hold
 * only whatever the Transcription tab had switched on, which made a perfectly
 * reasonable setup impossible to say out loud: the free Tartu speaker on four
 * announcements and the company's own cloned ElevenLabs voice on the fifth. A
 * voice now carries its provider in its value, so picking one IS choosing who
 * speaks it, and nobody has to walk to another tab and move an account-wide
 * switch to reach a voice.
 *
 * Two presses, not one, and deliberately: generating writes the recording and
 * puts its address in the field, and Save is what makes callers hear it. The gap
 * between them is where the operator listens to the preview. A single button
 * that did both would put an unheard sentence on the phone.
 *
 * The state this component owns is only what the server cannot yet know - the
 * clip made in this form and not saved. Everything else (which recording the
 * field points at, what it says, who spoke it) is derived, so nothing here can
 * disagree with the field above it.
 */
import { useState } from "react";
import { AudioLines, RefreshCw, Undo2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { ApiError } from "../../../../lib/api";
import {
  generateNoticeClip,
  type NoticeClipInfo,
  type NoticeSlot,
  type NoticeLocale,
  type NoticeVariant,
} from "../../../../lib/calls";
import { Btn, PickerField } from "../../../ui";
import { fmtBytes } from "../../../ui/format";
import { Note } from "../panel/fields";
import {
  canGenerate,
  clipForUrl,
  clipIsStale,
  clipSeconds,
  initialVoice,
  resolveVoiceId,
  voiceLabelIn,
  voiceOptions,
} from "./noticeClip";
import { useTtsVoices } from "./useTtsVoices";

/**
 * What each failure the endpoint can report is called on screen.
 *
 * Named one by one rather than shown as a code, because they are different
 * problems with different next steps: wait a minute, try again, paste a key with
 * synthesis rights, change the model id, tell whoever runs the server, or fix
 * the text. A single "generation failed" leaves the operator pressing the same
 * button forever - which is exactly what this row did the first time somebody
 * picked an ElevenLabs voice, because every one of that provider's own codes was
 * missing from this table and fell through to the generic line.
 *
 * ⚠ THIS TABLE HAS TO COVER `failure()` IN `tts.routes.ts` COMPLETELY. The two
 * providers share a code wherever the operator's next step is the same, and
 * have their own wherever it is not, and a code the route can send but this
 * cannot name is a dead end dressed as a retry.
 */
const ERROR_KEYS: Record<string, string> = {
  rate_limited: "settings.notice.clip.errRate",
  tts_timeout: "settings.notice.clip.errTimeout",
  tts_unavailable: "settings.notice.clip.errUnavailable",
  // Kept apart from the line above on purpose: one names the free university
  // service and tells somebody to wait, and that advice is wrong for a paid
  // account whose own provider is the one not answering.
  tts_eleven_unavailable: "settings.notice.clip.errElevenUnavailable",
  // Not a service failing at all - the plan is spent. It used to arrive here as
  // `tts_unavailable`, so the button reported an outage at a service the
  // request never reached and the actual fix went unsaid.
  tts_quota: "settings.notice.clip.errQuota",
  unknown_voice: "settings.notice.clip.errVoice",
  empty_text: "settings.notice.clip.errText",
  invalid_body: "settings.notice.clip.errText",
  encoder_missing: "settings.notice.clip.errEncoder",
  encode_failed: "settings.notice.clip.errEncoder",
  tts_no_key: "settings.notice.clip.errNoKey",
  tts_auth_failed: "settings.notice.clip.errAuth",
  tts_rejected: "settings.notice.clip.errRejected",
  clip_failed: "settings.notice.clip.errFailed",
  // The one refusal that is not a service failing. Every TartuNLP speaker is
  // Estonian and the service is never told which language it is reading, so the
  // clip it would return plays, previews and is unintelligible.
  tts_locale_unsupported: "settings.notice.clip.errLocale",
};

interface ClipFailure {
  key: string;
  /**
   * The service's own words, for the one failure where they are the whole
   * answer.
   *
   * ElevenLabs refuses a synthesis request with a validation message naming what
   * it disliked - a model the account has no access to, a voice outside the
   * plan, an output format it will not produce - and no wording of ours can
   * guess between those three. The route forwards it on `tts_rejected` and on
   * `tts_quota`, where the service's sentence carries the two numbers we cannot
   * know: credits left, and credits this recording needed.
   */
  detail: string;
}

function clipFailure(err: unknown): ClipFailure {
  const code = err instanceof ApiError && typeof err.message === "string" ? err.message : "";
  const body = err instanceof ApiError ? (err.body as { detail?: unknown } | null) : null;
  const detail = typeof body?.detail === "string" ? body.detail.trim() : "";
  return { key: ERROR_KEYS[code] ?? "settings.notice.clip.errFailed", detail };
}

export function NoticeClipRow({
  slot,
  locale,
  value,
  onChange,
  savedClip,
  builtInAudio,
}: {
  slot: NoticeSlot;
  /**
   * Which language tab this row belongs to. Absent is the base wording.
   *
   * Sent with the request rather than inferred from the text, because the
   * server refuses a non-Estonian recording while the account synthesises with
   * TartuNLP - every speaker that service has is Estonian, and it is never told
   * which language it is reading, so it would return a clip that plays and is
   * unintelligible. Knowing the language is what turns that into an error the
   * operator can act on.
   */
  locale?: NoticeLocale;
  value: NoticeVariant;
  onChange: (next: NoticeVariant) => void;
  /** What the server said about this announcement's recording on the last load. */
  savedClip?: NoticeClipInfo;
  /** The address of the recording that ships with the app for this slot. */
  builtInAudio: string;
}) {
  const t = useT();
  const { listing, failed } = useTtsVoices();

  // Null means "nothing chosen in this form yet", which is not the same as the
  // saved voice: the list arrives after the first paint, and only once it has
  // can we tell whether the saved voice is one the current provider offers.
  const [picked, setPicked] = useState<string | null>(null);
  const voice = picked ?? initialVoice(savedClip?.voice, listing);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ClipFailure | null>(null);
  // The recording made here and not saved yet. The server will not report it
  // until the form is saved, so without this the description under a clip the
  // operator just made would be the one it replaced.
  const [fresh, setFresh] = useState<NoticeClipInfo | null>(null);
  const [justMade, setJustMade] = useState(false);

  const clip = clipForUrl(value.audioUrl, savedClip, fresh);
  const stale = clipIsStale(clip, value.text);
  const usable = canGenerate(value);
  const onBuiltIn = value.audioUrl.trim() === builtInAudio;
  const voices = listing?.voices ?? [];
  // The heading each run of voices is shown under. Named here rather than in
  // `noticeClip.ts` because these are translated strings and that module is the
  // pure half - it sorts and annotates, it holds no copy.
  const options = voiceOptions(voices, voice, (provider) =>
    provider === "elevenlabs"
      ? t("settings.transcription.providerEleven")
      : t("settings.transcription.providerTartu"),
  );
  // No voice to generate with at all. Rarer than it used to be - TartuNLP needs
  // no key and falls back to a committed list - so reaching this means every
  // service is unreachable, and the button would otherwise post an empty voice
  // and come back with a 400 that says nothing about why.
  const noVoices = listing !== null && options.length === 0;
  const groups = listing?.groups ?? [];
  // The two ways a service can contribute nothing, kept apart because the next
  // step differs: one is a key somebody has to paste on another tab, the other
  // is a service that is simply not answering right now.
  const elevenNoKey = groups.some((g) => g.provider === "elevenlabs" && g.error === "no_key");
  const someoneOffline = groups.some((g) => !g.live && g.error !== "no_key");

  async function generate() {
    if (!usable || busy || noVoices) return;
    setBusy(true);
    setError(null);
    try {
      const made = await generateNoticeClip({ slot, locale, voice, text: value.text });
      // Described from what was sent plus what came back: the row holds exactly
      // this text and this speaker, so nothing has to be fetched to say so.
      setFresh({
        id: made.id,
        voice,
        bytes: made.bytes,
        durationMs: made.durationMs,
        createdAt: new Date().toISOString(),
        text: value.text,
        stale: false,
      });
      setJustMade(true);
      onChange({ ...value, audioUrl: made.audioUrl });
    } catch (err) {
      setError(clipFailure(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    /* A hairline block rather than a filled one. The obvious choice was the
       sunken surface every group in this section uses, and it made the voice
       picker disappear: `crm-inp` is that same colour, so the field and the
       panel behind it rendered as one flat rectangle with a word floating in
       it. A border separates without colliding. */
    <div className="grid gap-3 rounded-[20px] border border-[var(--crm-hair)] p-4">
      {/* Deliberately not wrapped in `Field`. That one is a `label`, and a
          `MenuField` is a button - a button inside a label has its clicks
          forwarded to the label's control, which here would mean a menu that
          never opens. The label is a plain span with the menu's own accessible
          name doing the work `for` would have done. */}
      <span className="crm-lab !mt-0">{t("settings.notice.clip.voice")}</span>

      <div className="flex flex-wrap items-center gap-2">
        {/* The app's own menu rather than a native select: this row sits between
            two raised pills, and the OS drop-down would drape a grey system list
            over them. Searchable rather than a plain list, because with both
            services offered at once this menu can be sixty voices long - which
            is exactly how ElevenLabs' own picker opens, on a search box. */}
        <PickerField
          value={voice}
          onChange={setPicked}
          options={options}
          label={t("settings.notice.clip.voice")}
          searchPlaceholder={t("settings.transcription.voiceSearch")}
          emptyLabel={t("settings.transcription.voiceNone")}
          className="min-w-[220px] max-w-[320px] flex-1"
        />
        <Btn tone="dark" onClick={generate} disabled={!usable || busy || noVoices}>
          {busy ? <RefreshCw className="size-3.5 animate-spin" /> : <AudioLines className="size-3.5" />}
          {busy
            ? t("settings.notice.clip.generating")
            : clip
              ? t("settings.notice.clip.regenerate")
              : t("settings.notice.clip.generate")}
        </Btn>
        {/* Only worth offering when it would change something. On the shipped
            recording already, this button is a no-op dressed as a way out - and
            for an announcement that HAS no shipped recording (the working-hours
            pair, which name one office's opening times and so could never be
            recorded in advance) it would be a button labelled "back to the
            shipped recording" that clears the field instead. */}
        {onBuiltIn || !builtInAudio ? null : (
          <Btn
            onClick={() => {
              onChange({ ...value, audioUrl: builtInAudio });
              setJustMade(false);
              setError(null);
            }}
            disabled={busy}
          >
            <Undo2 className="size-3.5" />
            {t("settings.notice.clip.builtIn")}
          </Btn>
        )}
      </div>

      <span className="crm-sub block text-[11.5px] leading-snug">{t("settings.notice.clip.hint")}</span>

      {/* An offline speaker list is worth saying: it is the same service that
          has to answer when the button is pressed, so this is advance warning
          rather than trivia. */}
      {/* An unkeyed ElevenLabs is a different problem from an offline TartuNLP
          and gets its own sentence: one is waiting, the other is a key somebody
          has to paste on another tab. Not a warning either - the menu below is
          full of usable Tartu speakers, and this only says which OTHER voices
          are missing from it and how to get them. */}
      {noVoices ? <Note tone="warn">{t("settings.notice.clip.noVoices")}</Note> : null}
      {someoneOffline && !noVoices ? (
        <Note tone="warn">{t("settings.notice.clip.offlineList")}</Note>
      ) : null}
      {elevenNoKey && !noVoices ? (
        <Note tone="mut">{t("settings.notice.clip.elevenNoKey")}</Note>
      ) : null}
      {failed ? <Note tone="warn">{t("settings.notice.clip.voicesFailed")}</Note> : null}

      {!usable ? <Note tone="mut">{t("settings.notice.clip.needsText")}</Note> : null}

      {clip ? (
        <div className="crm-sub text-[11.5px] leading-snug">
          {/* Resolved before it is named, so a clip recorded before voices
              carried their provider is still described by the speaker's name
              rather than by the bare id stored on the row. */}
          {t("settings.notice.clip.made", {
            voice: voiceLabelIn(voices, resolveVoiceId(clip.voice, listing) || clip.voice),
          })}
          {[clipSeconds(clip.durationMs), fmtBytes(clip.bytes)]
            .filter(Boolean)
            .map((part) => ` - ${part}`)
            .join("")}
        </div>
      ) : null}

      {/* The one thing audio cannot tell you by playing it: that it speaks a
          sentence the field no longer says. Only possible because the recording
          remembers its own script. */}
      {stale ? <Note tone="warn">{t("settings.notice.clip.stale")}</Note> : null}

      {justMade && !stale ? (
        <Note tone="ok" live>
          {t("settings.notice.clip.unsaved")}
        </Note>
      ) : null}

      {/* The service's own sentence rides along under ours where there is one.
          Truncated by the API to 300 characters and shown as it arrived: it is
          the only place that distinguishes "this account has no access to that
          model" from "that voice is not on your plan", and paraphrasing it
          would throw away the one fact worth having. */}
      {error ? (
        <Note tone="bad" live>
          <span className="block">{t(error.key)}</span>
          {error.detail ? (
            <span className="mt-1.5 block break-words font-mono text-[11.5px] opacity-80">
              {error.detail}
            </span>
          ) : null}
        </Note>
      ) : null}
    </div>
  );
}
